import type { Assignment, AssignmentEntry, AssignmentMeta } from '@lessonup/assignments-shared';
import { Timer } from '@lessonup/pin-renderer';
import { Box, PinComponent, PinComponentId, PinId, PinTheme, TeacherPin } from '@lessonup/pins-shared';
import { FocusMode } from './playerStoreFocusMode.types';
import { AsyncUpdatePlayerAction, PlayerStoreAction } from './reducers/storeReducer/storeReducer.types';
import { Listener, ListenerUnsubscribe } from './utils/listeners';

export type InputPinPhase = 'input' | 'results';
export const inputPinPhases = ['input', 'results'] as const;

export type InputAndDiscussPinPhase = 'input' | 'discuss' | 'results';
export const inputAndDiscussPinPhases = ['input', 'discuss', 'results'] as const;

export interface PinComponentState<P extends PinComponent = PinComponent> {
  settings: Partial<P['settings']>;
  layout: Partial<P['layout']>;
}

export interface PrepView {
  type: 'PREP';
  phase: 'prep';
}

export interface RankingView {
  type: 'RANKING';
  phase: 'ranking';
}

export interface QuizView {
  type: 'MULTIPLE_CHOICE';
  phase: InputAndDiscussPinPhase;
}

export interface SortingQuestionView {
  type: 'SORTING_QUESTION';
  phase: InputAndDiscussPinPhase;
}

export interface PollView {
  type: 'POLL';
  phase: InputPinPhase;
}

export interface FillInTheBlanksView {
  type: 'FILL_IN_THE_BLANKS';
  phase: InputPinPhase;
}

export interface OpenQuestionView {
  type: 'OPEN_QUESTION';
  phase: InputPinPhase | InputAndDiscussPinPhase;
  answerOrder: string[];
  revealedAnswers: string[];
  isCorrectAnswersCardRevealed: boolean;
}

export interface WordCloudViewAnswer {
  text: string;
  layout: Box;
  order: string;
}

export interface WordCloudView {
  type: 'WORD_CLOUD';
  phase: InputPinPhase;
}

export interface AnswerByCategoryViewAnswer {
  key: string;
  text: string;
  layout: Box;
  order: string;
  category: string;
}
export interface AnswerByCategoryView {
  type: 'ANSWER_BY_CATEGORY';
  phase: InputPinPhase;
}

export interface DragAndDropView {
  type: 'DRAG_AND_DROP';
  phase: InputAndDiscussPinPhase;
}

export interface NonInteractiveView {
  type: 'NON_INTERACTIVE';
  phase: 'base';

  /** The most recently played animation, or null if no animation has been played yet. */
  lastPlayedAnimationStep: AnimationStepRef | null;
}

/** Reference to a step in a component animation. */
export type AnimationStepRef = {
  /** The id of the component that the animation belongs to. */
  componentId: PinComponentId;

  /**
   * The current step of the animation, 0 <= step < n.
   *
   * For example, for text animations by paragraph, the steps iterate through
   * the various stages of the animation.
   *
   * Most animations consist of a single step, in which case this value is
   * always 0.
   */
  step: number;
};

export type AnyPinView =
  | NonInteractiveView
  | QuizView
  | OpenQuestionView
  | WordCloudView
  | AnswerByCategoryView
  | PollView
  | SortingQuestionView
  | FillInTheBlanksView
  | DragAndDropView;
export type AnyMetaView = PrepView | RankingView;
export type AnyView = AnyPinView | AnyMetaView;
export type AnyViewType = AnyView['type'];

type ViewMappings = {
  MULTIPLE_CHOICE: QuizView;
  OPEN_QUESTION: OpenQuestionView;
  WORD_CLOUD: WordCloudView;
  ANSWER_BY_CATEGORY: AnswerByCategoryView;
  POLL: PollView;
  SORTING_QUESTION: SortingQuestionView;
  FILL_IN_THE_BLANKS: FillInTheBlanksView;
  DRAG_AND_DROP: DragAndDropView;
  NON_INTERACTIVE: NonInteractiveView;
};

export type ViewTypeToView<T extends AnyViewType> = T extends keyof ViewMappings ? ViewMappings[T] : never;

export type Student = {
  name: string;
  userId: string;
};

export type BackgroundActionState = 'done' | 'loading' | 'error';
export type BackgroundAction = {
  state: BackgroundActionState;
  locked: boolean;
  action: () => Promise<void>;
};

export interface PinState {
  view: Prettify<AnyView> | null;
  componentState: Record<string, Partial<Prettify<PinComponentState>>>;
  answerOrder?: string[];
  wordCloud?: {
    answers: WordCloudViewAnswer[];
    revealedAnswers: string[];
    deletedAnswers: string[];
    lastOrder: string;
  };
  answerByCategory?: {
    answers: AnswerByCategoryViewAnswer[];
    revealedAnswers: string[];
    deletedAnswers: string[];
    lastOrder: string;
  };
  dragAndDrop?: {
    clonedComponents?: Record<string, PinComponent & { original_id?: string }>;
  };
}

export interface PlayerStoreNavigationState {
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface PlayerStoreState {
  assignmentId: string;
  assignment: Assignment.Realtime | null;
  assignmentEntries: AssignmentEntry[];
  assignmentMeta: AssignmentMeta.Realtime | null;
  backgroundAction: BackgroundAction;
  currentView: AnyView | null;
  currentPinId: string | null;
  pinState: Record<string, PinState>;
  pinIdsOrder: string[];
  currentStudents: Student[];
  lockPlayerNavigation: boolean;
  groupId: string | null;
  navState: PlayerStoreNavigationState;
  selectedPinComponentId: string | null;
  focusMode: FocusMode | null;
  selection: {
    type: SelectionType | null;
    ids: string[];
  };
  timers: Record<string, Timer.State>; // indexed by timer pin component id
  pinThemes: Record<string, PinTheme>;
  defaultPinTheme: PinTheme;
}

export interface YjsPlayerInterface {
  lockPlayerNavigation: boolean;
  currentView: Prettify<AnyView> | null;
  currentPinId: string | null;
  pinState: Prettify<Record<string, Prettify<PinState>>>;
  navState: PlayerStoreNavigationState;
  focusMode: Prettify<FocusMode> | null;
  timers: Prettify<Record<string, Timer.State>>; // indexed by timer pin component id
  selection?: {
    type: SelectionType | null;
    ids: string[];
  };
}

export type YjsPlayer = Prettify<YjsPlayerInterface>;

/**
 * Currently this Prettifier is needed because it fixes some sort of mismatch within our schemas.
 * @TODO investigate this mismatch.
 */
type Prettify<T> = {
  [K in keyof T]: T[K] extends object ? Prettify<T[K]> : T[K];
} & object;

export interface InitializedPlayerStoreState extends PlayerStoreState {
  assignmentMeta: AssignmentMeta.Realtime;
  assignment: Assignment.Realtime;
  currentPinId: string;
}

export type SelectionType = 'pinComponent' | 'selectableElement';

export interface SelectedItem {
  type: SelectionType;
  id: string;
}

export type GetRawPinSliceOutput = {
  previousPins: TeacherPin[];
  nextPins: TeacherPin[];
};

export type PlayerStoreDispatcher<A extends PlayerStoreAction = PlayerStoreAction> = (
  action: A
) => A extends AsyncUpdatePlayerAction ? Promise<void> : void;

export interface PlayerStore {
  assignment: () => Assignment.Realtime | null;
  assignmentEntries: () => AssignmentEntry[];
  assignmentMeta: () => AssignmentMeta.Realtime | null;
  closePlayer: () => void;
  componentState: () => PinState['componentState'];
  currentNavigationState: () => PlayerStoreNavigationState;
  currentPinId: () => PinComponentId | null;
  currentPin: () => TeacherPin | null;
  getPinTheme: (pinId?: PinId) => PinTheme;
  currentPinState: () => PinState | null;
  currentView: () => AnyView | null;
  focusMode: () => FocusMode | null;
  dispatch: PlayerStoreDispatcher;
  getBackgroundActionState: () => BackgroundActionState;
  getCurrentStudents: () => Student[];
  getLockedNavigation: () => boolean;
  getPinIds: () => PinId[];
  getSelectedPinComponentId: () => PinComponentId | null;
  setSelectedPinComponentId: (pinComponentId: PinComponentId | null) => void;
  getRemainingStudentCount: () => number;
  nextPin: () => TeacherPin | null;
  getRawPinSlices: (currentPinId: string, slices: number) => GetRawPinSliceOutput;
  isPlayerLoading: () => boolean;
  subscribeAssignment: (listener: Listener) => ListenerUnsubscribe;
  subscribePinThemes: (listener: Listener) => ListenerUnsubscribe;
  subscribeBackgroundAction: (listener: Listener) => ListenerUnsubscribe;
  subscribeEntries: (listener: Listener) => ListenerUnsubscribe;
  subscribeSelectedPinComponentId: (listener: Listener) => ListenerUnsubscribe;
  setPlayerNavigationLock: (lock: boolean) => void;
  getSelectedItems: () => string[];
  getSelectionType: () => SelectionType | null;
  toggleSelectedItem: (type: SelectionType, id: string) => void;
  clearSelection: () => void;
  subscribeSelectedItems: (listener: Listener) => ListenerUnsubscribe;
  timerState(pinId: string, timerPinComponentId: string): Timer.State;
}

export interface PlayerStoreSetters {
  setAssignmentMeta: (data: AssignmentMeta.Realtime) => void;
  setAssignmentEntries: (data: AssignmentEntry[]) => void;
  setAssignment: (data: Assignment.Realtime, onSetAssignment?: (state: PlayerStoreState) => void) => void;
  setOnClose: (onCloseCallback: () => void) => void;
  syncData: (data: YjsPlayer) => void;
}

export interface PlayerStoreActions {
  closePlayer: () => void;
  resetBackgroundAction: () => void;
  setBackgroundActionState: (backgroundActionState: BackgroundActionState) => void;
  setBackgroundActionAction: (action: () => Promise<void>) => void;
  setBackgroundActionLock: (lock: boolean) => void;
}
